import React, { useEffect, useState } from "react";
import { Plus, UserPlus, CalendarCheck, Loader2, Wallet, Banknote } from "lucide-react";
import { toast } from "sonner";
import api, { TRY, formatApiError } from "@/lib/api";
import { Card } from "@/components/Cards";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import StaffCard from "@/components/staff/StaffCard";
import StaffForm from "@/components/staff/StaffForm";
import StaffTxnForm from "@/components/staff/StaffTxnForm";
import StaffLedgerTable from "@/components/staff/StaffLedgerTable";
import StaffLeaveDialog from "@/components/staff/StaffLeaveDialog";
import PayrollDialog from "@/components/staff/PayrollDialog";
import BulkPayDialog from "@/components/staff/BulkPayDialog";
import LeaveCalendar from "@/components/staff/LeaveCalendar";
import SearchBox, { matches } from "@/components/SearchBox";
import { takeJump } from "@/lib/jump";

const monthISO = () => new Date().toISOString().slice(0, 7);

export default function Staff() {
  const [staff, setStaff] = useState([]);
  const [filter, setFilter] = useState(() => takeJump("staff")?.id || "ALL");
  const [txns, setTxns] = useState([]);
  const [summary, setSummary] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [txnOpen, setTxnOpen] = useState(false);
  const [editTxn, setEditTxn] = useState(null);
  const [txnType, setTxnType] = useState("payment");
  const [month, setMonth] = useState(monthISO());
  const [accruing, setAccruing] = useState(false);
  const [leaveStaff, setLeaveStaff] = useState(null);
  const [payrollStaff, setPayrollStaff] = useState(null);
  const [lastRun, setLastRun] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [tick, setTick] = useState(0);
  const [search, setSearch] = useState("");
  const shownStaff = staff.filter((s) => matches(search, s.name, s.role, s.phone));
  const shownTxns = txns.filter((t) => matches(search, t.staff_name, t.description, t.type, t.amount));

  const loadAll = async () => {
    const [s, t, sm] = await Promise.all([
      api.get("/staff"),
      api.get("/staff-ledger", { params: filter !== "ALL" ? { staff_id: filter } : {} }),
      api.get("/staff/summary"),
    ]);
    setStaff(s.data); setTxns(t.data); setSummary(sm.data); setTick((x) => x + 1);
    api.get("/cron/runs").then((r) => setLastRun(r.data.find((x) => x.status === "done") || null)).catch(() => {});
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [filter]);

  const accrue = async () => {
    setAccruing(true);
    try {
      const { data } = await api.post("/staff-ledger/accrue-salaries", { month });
      toast.success(`${data.message} (${TRY(data.total)})`);
      loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAccruing(false); }
  };

  const removeStaff = async (id) => {
    try { await api.delete(`/staff/${id}`); toast.success("Personel silindi"); if (filter === id) setFilter("ALL"); loadAll(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openTxn = (type) => { setTxnType(type); setTxnOpen(true); };

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Personel Cari Hesapları</h1>
          <p className="text-sm text-muted-foreground mt-1">Maaş / hakediş tahakkukları, ödemeler, avanslar ve kalan borç takibi.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => { setEditItem(null); setFormOpen(true); }} data-testid="staff-add-btn" className="rounded-full h-11 px-5 gap-2">
            <UserPlus className="h-4 w-4" /> Yeni Personel
          </Button>
          <Button variant="outline" onClick={() => openTxn("accrual")} data-testid="staff-add-accrual-btn" className="rounded-full h-11 px-5 gap-2 text-rose-400 border-rose-400/40">
            <Plus className="h-4 w-4" /> Hakediş Ekle
          </Button>
          <Button onClick={() => openTxn("payment")} data-testid="staff-add-payment-btn" className="rounded-full h-11 px-6 font-semibold gap-2">
            <Wallet className="h-4 w-4" /> Ödeme Yap
          </Button>
          <Button variant="secondary" onClick={() => setBulkOpen(true)} data-testid="bulk-pay-btn" className="rounded-full h-11 px-5 gap-2">
            <Banknote className="h-4 w-4" /> Tüm Maaşları Öde
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Mini label="Personele Toplam Borç" value={TRY(summary?.payable)} color="text-amber-400" testId="staff-kpi-payable" />
        <Mini label="Toplam Tahakkuk" value={TRY(summary?.total_accrued)} color="text-rose-400" testId="staff-kpi-accrued" />
        <Mini label="Toplam Ödenen" value={TRY(summary?.total_paid)} color="text-emerald-400" testId="staff-kpi-paid" />
        <Mini label="Bu Ay Ödenen (Maliyet)" value={TRY(summary?.month_paid)} color="text-sky-400" testId="staff-kpi-month-paid" />
      </div>

      <Card className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-2 text-sm font-medium"><CalendarCheck className="h-4 w-4 text-primary" /> Aylık Maaş Tahakkuku</div>
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-10 w-[170px] font-mono" data-testid="accrue-month-input" />
        <Button onClick={accrue} disabled={accruing} data-testid="accrue-salaries-btn" className="h-10 gap-2">
          {accruing && <Loader2 className="h-4 w-4 animate-spin" />} Tüm Personelin Maaşını İşle
        </Button>
        <span className="text-xs text-muted-foreground" data-testid="auto-accrual-info">
          Her ayın son günü 23:00'te otomatik çalışır (ör. Eylül maaşı 30 Eylül 23:00); mükerrer kayıt oluşmaz.
          {lastRun && <> Son otomatik çalışma: {new Date(lastRun.finished_at).toLocaleString("tr-TR")} — {lastRun.result?.message}</>}
        </span>
      </Card>

      <SearchBox value={search} onChange={setSearch} placeholder="Personel adı, açıklama ara…" testId="staff-search-input" className="w-full sm:w-[320px]" />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {staff.length === 0 && <Card className="p-8 text-center text-muted-foreground col-span-full" data-testid="staff-empty">Henüz personel eklenmedi</Card>}
        {staff.length > 0 && shownStaff.length === 0 && <Card className="p-6 text-center text-muted-foreground col-span-full text-sm" data-testid="staff-search-empty">Aramayla eşleşen personel yok</Card>}
        {shownStaff.map((s) => (
          <StaffCard key={s.id} s={s} selected={filter === s.id} onSelect={() => setFilter(filter === s.id ? "ALL" : s.id)}
            onEdit={() => { setEditItem(s); setFormOpen(true); }} onDelete={() => removeStaff(s.id)}
            onLeave={() => setLeaveStaff(s)} onPayroll={() => setPayrollStaff(s)} />
        ))}
      </div>

      <div className="flex items-center gap-3">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="h-11 w-full sm:w-[280px]" data-testid="staff-ledger-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Tüm Personel</SelectItem>
            {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <StaffLedgerTable txns={shownTxns} onChanged={loadAll} onEdit={(t) => { setEditTxn(t); setTxnType(t.type); setTxnOpen(true); }} />

      <LeaveCalendar refreshKey={tick} />

      <BulkPayDialog open={bulkOpen} onClose={() => setBulkOpen(false)} onDone={loadAll} />

      <StaffForm open={formOpen} onClose={() => setFormOpen(false)} item={editItem} onSaved={loadAll} />
      <StaffTxnForm open={txnOpen} onClose={() => { setTxnOpen(false); setEditTxn(null); }} type={txnType} staff={staff} editItem={editTxn}
        defaultStaffId={filter !== "ALL" ? filter : ""} onSaved={loadAll} />
      <StaffLeaveDialog staff={leaveStaff ? staff.find((x) => x.id === leaveStaff.id) || leaveStaff : null} onClose={() => setLeaveStaff(null)} onChanged={loadAll} />
      <PayrollDialog staff={payrollStaff} onClose={() => setPayrollStaff(null)} />
    </div>
  );
}

const Mini = ({ label, value, color, testId }) => (
  <Card className="p-4" data-testid={testId}>
    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className={`mt-2 font-mono text-lg sm:text-xl font-bold ${color}`}>{value}</div>
  </Card>
);
